/* eslint-disable @typescript-eslint/no-explicit-any */
import { useMemo, useRef, useState, useCallback } from 'react';
import {
  Accordion,
  AccordionItem,
  AccordionButton,
  AccordionPanel,
  AccordionIcon,
  Alert,
  AlertDescription,
  AlertDialog,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  AlertIcon,
  AlertTitle,
  Badge,
  Box,
  Button,
  Card,
  Flex,
  HStack,
  Icon,
  IconButton,
  Input,
  InputGroup,
  InputLeftElement,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  Spinner,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tooltip,
  Tr,
  useDisclosure,
  useToast,
} from '@chakra-ui/react';
import {
  FaCheckCircle,
  FaTimesCircle,
  FaSearch,
  FaFileExport,
  FaPlus,
  FaMapSigns,
  FaSyncAlt,
  FaClipboardList,
  FaTrash,
  FaLayerGroup,
  FaSort,
  FaSortUp,
  FaSortDown,
  FaCopy,
} from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import { useVirtualizer } from '@tanstack/react-virtual';
import { useQueryClient } from 'react-query';
import { useTheme } from '@partiu/ui/contexts/ThemeProvider.tsx';
import {
  DashboardHooks,
  useAgreementData,
  useAgreementPlans,
  useAllMapIntegrationPlans,
  useErpPlans,
  useErpPlansReports,
  useSyncErpPlansMutation,
  useDeleteMapIntegrationPlanMutation,
} from '@partiu/api/dashboard/hooks.ts';
import {
  IMapIntegrationPlanItem,
  IPlan,
  IPlanMappingRow,
  IReportItem,
} from '@partiu/api/dashboard/types.ts';
import { downloadCSV, CSVColumn } from '@partiu/ui/utils.ts';
import { CreateMapIntegrationPlanModal, CreateMapIntegrationPlanModalRef } from './CreateMapIntegrationPlanModal.tsx';
import { BulkMapIntegrationPlanModal } from './BulkMapIntegrationPlanModal.tsx';
import { ValueRangeFilter } from '../ProductsAndPlans/components/atoms/ValueRangeFilter.tsx';
import { ClientNumberFilter } from '../ProductsAndPlans/components/atoms/ClientNumberFilter.tsx';
import { filterAndSortBySearchRelevance, getPlanSlugIntegrationForPayload } from './utils/searchPlanMapping.ts';

type FilterType = 'all' | 'mapped' | 'unmapped';

function getPlanName(planId: number, plansMap: Map<number, IPlan>): string {
  const plan = plansMap.get(planId);
  if (!plan) return `Plano #${planId}`;
  return plan.displayName || plan.display_name || plan.name || `Plano #${planId}`;
}

function formatDate(dateStr: string): string {
  if (!dateStr) return '-';
  try {
    return new Date(dateStr).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric' });
  } catch {
    return dateStr;
  }
}

function formatDateTime(dateStr: string | Date): string {
  if (!dateStr) return '-';
  try {
    const d = typeof dateStr === 'string' ? new Date(dateStr) : dateStr;
    return d.toLocaleDateString('pt-BR', {
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  } catch {
    return String(dateStr);
  }
}

function getSyncReportName(): string {
  return `Sincronização ERP - ${new Date().toLocaleDateString('pt-BR')} ${new Date().toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}`;
}

function formatPrice(value?: number | string): string {
  if (value === undefined || value === null) return '-';
  const str = typeof value === 'string' ? value.replace(/\s*R\$\s*/gi, '').trim() : String(value);
  const num = typeof value === 'string' ? parseFloat(str.replace(',', '.')) : value;
  if (isNaN(num)) return typeof value === 'string' ? value : '-';
  return num.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
}

function buildMappingIndex(mappings: IMapIntegrationPlanItem[]): Map<string, IMapIntegrationPlanItem[]> {
  const index = new Map<string, IMapIntegrationPlanItem[]>();
  for (const m of mappings) {
    const slug = m.plan_slug_integration;
    if (!slug) continue;
    const list = index.get(slug);
    if (list) list.push(m);
    else index.set(slug, [m]);
  }
  return index;
}

type SortField = 'price' | 'count_clients_active' | null;
type SortDirection = 'asc' | 'desc';

export function PlanMappingPage() {
  const { id, colors, integrationId } = useTheme();
  const [searchTerm, setSearchTerm] = useState('');
  const [filter, setFilter] = useState<FilterType>('all');
  const [minValue, setMinValue] = useState<number | null>(null);
  const [maxValue, setMaxValue] = useState<number | null>(null);
  const [minClientNumber, setMinClientNumber] = useState<number | null>(null);
  const [maxClientNumber, setMaxClientNumber] = useState<number | null>(null);
  const [sortField, setSortField] = useState<SortField>(null);
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');
  const [mappingToDelete, setMappingToDelete] = useState<IMapIntegrationPlanItem | null>(null);
  const modalRef = useRef<CreateMapIntegrationPlanModalRef>(null);
  const toast = useToast();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const syncModal = useDisclosure();
  const [pendingSync, setPendingSync] = useState<IReportItem | null>(null);
  const deleteDialog = useDisclosure();
  const bulkModal = useDisclosure();
  const cancelDeleteRef = useRef<HTMLButtonElement>(null);
  const [expandedAccordionIndexes, setExpandedAccordionIndexes] = useState<number[]>([]);
  const listParentRef = useRef<HTMLDivElement>(null);

  const { data: agreementData } = useAgreementData();
  const erpName = agreementData?.integrationId || integrationId || '';

  const { data: erpData, isLoading: isLoadingErp, isError: isErrorErp } = useErpPlans();
  const { data: allMappings, isLoading: isLoadingMappings } = useAllMapIntegrationPlans();
  const { data: agreementPlansData } = useAgreementPlans();

  const erpPlans = erpData?.plans;

  const erpReportsQueryKey = useMemo(() => [DashboardHooks.Reports, 'erp-plans-dashboard'], []);

  const syncMutation = useSyncErpPlansMutation({
    onSuccess: async () => {
      queryClient.refetchQueries([DashboardHooks.ErpPlans]);
      queryClient.refetchQueries([DashboardHooks.PlansIntegration]);
      queryClient.refetchQueries([DashboardHooks.Modules]);
      await queryClient.refetchQueries(erpReportsQueryKey);
      setPendingSync(null);
      syncModal.onOpen();
    },
    onError: async () => {
      await queryClient.invalidateQueries(erpReportsQueryKey);
      setPendingSync(null);
      toast({ title: 'Erro ao solicitar sincronização', description: 'Tente novamente.', status: 'error', duration: 4000 });
    },
  });

  const { data: erpReportsData, isLoading: isLoadingErpReports } = useErpPlansReports();
  const erpReports = erpReportsData?.results ?? [];
  const latestErpReport = erpReports[0];
  const displayReport = pendingSync ?? latestErpReport;

  const handleSyncClick = useCallback(() => {
    const fakeEntry: IReportItem = {
      name: getSyncReportName(),
      type: 'erp-plans-dashboard',
      status: 'pending',
      createdAt: new Date().toISOString(),
    };
    setPendingSync(fakeEntry);

    queryClient.setQueryData(erpReportsQueryKey, (old: any) => {
      const prev = old ?? { results: [], count: 0 };
      return {
        ...prev,
        results: [fakeEntry, ...(prev.results ?? [])],
        count: (prev.count ?? 0) + 1,
      };
    });

    syncMutation.mutate();
  }, [syncMutation, queryClient, erpReportsQueryKey]);

  const deleteMappingMutation = useDeleteMapIntegrationPlanMutation({
    onSuccess: () => {
      queryClient.refetchQueries([DashboardHooks.AllMapIntegrationPlans]);
      queryClient.refetchQueries([DashboardHooks.PlansIntegration]);
      setMappingToDelete(null);
      deleteDialog.onClose();
      toast({ title: 'Mapeamento excluído', status: 'success', duration: 3000 });
      queryClient.refetchQueries([DashboardHooks.Modules]);
    },
    onError: () => {
      toast({ title: 'Erro ao excluir mapeamento', description: 'Tente novamente.', status: 'error', duration: 4000 });
    },
  });

  const handleConfirmDelete = useCallback(() => {
    if (mappingToDelete) {
      deleteMappingMutation.mutate(mappingToDelete.id);
    }
  }, [mappingToDelete, deleteMappingMutation]);

  const handleOpenDelete = useCallback((m: IMapIntegrationPlanItem) => {
    setMappingToDelete(m);
    deleteDialog.onOpen();
  }, [deleteDialog]);

  const plansMap = useMemo(() => {
    const map = new Map<number, IPlan>();
    const plans = (agreementPlansData as any)?.results ?? agreementPlansData ?? [];
    if (Array.isArray(plans)) {
      plans.forEach((plan: IPlan) => map.set(plan.id, plan));
    }
    return map;
  }, [agreementPlansData]);

  const mappingIndex = useMemo(() => buildMappingIndex(allMappings || []), [allMappings]);

  const rows: IPlanMappingRow[] = useMemo(() => {
    if (!erpPlans) return [];
    const mapped = erpPlans.map((erpPlan) => {
      const planMappings = mappingIndex.get(erpPlan.slug) || [];
      return { ...erpPlan, isMapped: planMappings.length > 0, mappings: planMappings };
    });
    mapped.sort((a, b) => (b.count_clients ?? 0) - (a.count_clients ?? 0));
    return mapped;
  }, [erpPlans, mappingIndex]);

  const totalMapped = useMemo(() => rows.filter((r) => r.isMapped).length, [rows]);
  const totalUnmapped = useMemo(() => rows.filter((r) => !r.isMapped).length, [rows]);

  const clearValueFilter = useCallback(() => {
    setMinValue(null);
    setMaxValue(null);
  }, []);

  const clearClientNumberFilter = useCallback(() => {
    setMinClientNumber(null);
    setMaxClientNumber(null);
  }, []);

  const isValueFilterActive = minValue !== null || maxValue !== null;
  const isClientNumberFilterActive = minClientNumber !== null || maxClientNumber !== null;

  const handleSort = useCallback((field: SortField) => {
    if (sortField === field) {
      setSortDirection((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortField(field);
      setSortDirection('desc');
    }
  }, [sortField]);

  const filteredRows = useMemo(() => {
    let result = rows;

    // Filtro por tipo (mapped/unmapped)
    if (filter === 'mapped') result = result.filter((r) => r.isMapped);
    if (filter === 'unmapped') result = result.filter((r) => !r.isMapped);

    // Filtro por busca de texto (regex + ordenação por relevância: exato primeiro, depois por similaridade)
    if (searchTerm?.trim()) {
      result = filterAndSortBySearchRelevance(result, searchTerm, true);
    }

    // Filtro por range de valores
    if (isValueFilterActive) {
      result = result.filter((row) => {
        const planValue = typeof row.price === 'number' ? row.price : (typeof row.price === 'string' ? parseFloat(String(row.price).replace(',', '.')) : 0);
        if (isNaN(planValue)) return true;

        if (minValue !== null && maxValue !== null) {
          return planValue >= minValue && planValue <= maxValue;
        } else if (minValue !== null) {
          return planValue >= minValue;
        } else if (maxValue !== null) {
          return planValue <= maxValue;
        }
        return true;
      });
    }

    // Filtro por range de número de clientes
    if (isClientNumberFilterActive) {
      result = result.filter((row) => {
        const clientCount = row.count_clients ?? 0;

        if (minClientNumber !== null && maxClientNumber !== null) {
          return clientCount >= minClientNumber && clientCount <= maxClientNumber;
        } else if (minClientNumber !== null) {
          return clientCount >= minClientNumber;
        } else if (maxClientNumber !== null) {
          return clientCount <= maxClientNumber;
        }
        return true;
      });
    }

    // Ordenação (só aplica quando não há busca - a busca já ordena por relevância)
    if (sortField && !searchTerm?.trim()) {
      result = [...result].sort((a, b) => {
        let aValue: number = 0;
        let bValue: number = 0;

        if (sortField === 'price') {
          aValue = typeof a.price === 'number' ? a.price : (typeof a.price === 'string' ? parseFloat(String(a.price).replace(',', '.')) : 0);
          bValue = typeof b.price === 'number' ? b.price : (typeof b.price === 'string' ? parseFloat(String(b.price).replace(',', '.')) : 0);
          if (isNaN(aValue)) aValue = 0;
          if (isNaN(bValue)) bValue = 0;
        } else if (sortField === 'count_clients_active') {
          aValue = a.count_clients_active ?? 0;
          bValue = b.count_clients_active ?? 0;
        }

        if (sortDirection === 'asc') {
          return aValue - bValue;
        } else {
          return bValue - aValue;
        }
      });
    }

    return result;
  }, [rows, filter, searchTerm, minValue, maxValue, minClientNumber, maxClientNumber, isValueFilterActive, isClientNumberFilterActive, sortField, sortDirection]);

  const handleExportCSV = useCallback(() => {
    const csvColumns: CSVColumn[] = [
      { field: 'isMapped', headerName: 'Mapeado', valueFormatter: (v: boolean) => (v ? 'Sim' : 'Não') },
      { field: 'mappingsCount', headerName: 'Qtd Mapeamentos' },
      { field: 'name', headerName: 'Nome (ERP)' },
      { field: 'slug', headerName: 'Slug (ERP)' },
      { field: 'price', headerName: 'Preço (ERP)', valueFormatter: (v: number) => v != null ? v.toLocaleString('pt-BR', { minimumFractionDigits: 2 }) : '' },
      { field: 'count_clients', headerName: 'Clientes' },
      { field: 'count_clients_active', headerName: 'Clientes Ativos' },
      { field: 'active', headerName: 'Status', valueFormatter: (v: boolean) => (v ? 'Ativo' : 'Inativo') },
    ];
    downloadCSV('mapeamento-planos', csvColumns, (filteredRows || []).map((r) => ({ ...r, mappingsCount: r.mappings?.length ?? 0 })));
  }, [filteredRows]);

  const rowsToRender = filteredRows || [];
  const rowVirtualizer = useVirtualizer({
    count: rowsToRender.length,
    getScrollElement: () => listParentRef.current,
    estimateSize: () => 72,
    overscan: 10,
    measureElement: (el) => el?.getBoundingClientRect().height,
  });
  const virtualItems = rowVirtualizer.getVirtualItems();

  const expandedPositions = useMemo(
    () =>
      expandedAccordionIndexes
        .map((idx) => virtualItems.findIndex((v) => v.index === idx))
        .filter((i) => i >= 0),
    [expandedAccordionIndexes, virtualItems]
  );

  const handleAccordionChange = useCallback(
    (expanded: number[]) => {
      setExpandedAccordionIndexes((prev) => {
        const expandedInView = expanded.map((pos) => virtualItems[pos]?.index).filter((i) => i !== undefined) as number[];
        const visibleIndices = new Set(virtualItems.map((v) => v.index));
        const expandedOutOfView = prev.filter((idx) => !visibleIndices.has(idx));
        return [...expandedOutOfView, ...expandedInView];
      });
    },
    [virtualItems]
  );

  if (!integrationId) {
    return (
      <Flex direction="column" align="center" justify="center" w="100%" minH="50vh">
        <Alert status="info" maxW="600px" borderRadius="md">
          <AlertIcon />
          <Box>
            <AlertTitle>Integração não disponível</AlertTitle>
            <AlertDescription>
              Esta funcionalidade está disponível apenas para convênios com integração configurada.
            </AlertDescription>
          </Box>
        </Alert>
      </Flex>
    );
  }

  const isLoading = isLoadingErp || isLoadingMappings;

  return (
    <>
      <Flex direction="column"  w="100%" p={4}>
        <Box mb={4}>
          <Card borderRadius={8} borderWidth="1px" borderColor="gray.200" overflow="hidden">
            <Flex
              align="center"
              justify="space-between"
              px={4}
              py={3}
              bg="gray.50"
              borderBottomWidth="1px"
              borderColor="gray.100"
            >
              <HStack spacing={3}>
                <Icon as={FaClipboardList} color="blue.500" boxSize={4} />
                <Text fontSize="sm" fontWeight="600" color="gray.700">
                  Sincronizações ERP
                </Text>
                {erpName && (
                  <Badge colorScheme="purple" variant="subtle" fontSize="2xs" px={1.5} py={0.5} borderRadius="md">
                    {erpName}
                  </Badge>
                )}
                {displayReport && (
                  <Badge
                    colorScheme={
                      displayReport.status === 'error' || displayReport.status === 'failed'
                        ? 'red'
                        : displayReport.status === 'done'
                          ? 'green'
                          : 'blue'
                    }
                    variant="subtle"
                    fontSize="2xs"
                  >
                    {displayReport.status === 'done'
                      ? 'Finalizado'
                      : displayReport.status === 'error' || displayReport.status === 'failed'
                        ? 'Erro'
                        : displayReport.status === 'created'
                          ? 'Criado'
                          : 'Pendente'}
                  </Badge>
                )}
              </HStack>
              <HStack spacing={2}>
                <Tooltip label="Solicitar nova sincronização dos planos do ERP" hasArrow>
                  <Button
                    leftIcon={<FaSyncAlt />}
                    size="sm"
                    colorScheme="blue"
                    variant="outline"
                    onClick={handleSyncClick}
                    isLoading={syncMutation.isLoading}
                    loadingText="Solicitando..."
                  >
                    Sincronizar
                  </Button>
                </Tooltip>
                <Button
                  size="xs"
                  variant="ghost"
                  colorScheme="blue"
                  rightIcon={<FaClipboardList />}
                  onClick={() => navigate('/relatorios')}
                >
                  Ver relatórios
                </Button>
              </HStack>
            </Flex>
            <Box px={4} py={3}>
              {isLoadingErpReports ? (
                <Flex align="center" gap={2}>
                  <Spinner size="sm" color={colors.primaryColor} />
                  <Text fontSize="xs" color="gray.500">Carregando...</Text>
                </Flex>
              ) : displayReport ? (
                <>
                  {displayReport.name && (
                    <Text fontSize="xs" color="gray.600" mb={1} noOfLines={1}>
                      {displayReport.name}
                    </Text>
                  )}
                  {displayReport.status === 'pending' && (
                    <HStack spacing={2} mt={1}>
                      <Spinner size="xs" color="blue.500" />
                      <Text fontSize="xs" color="blue.600" fontWeight="500">
                        Sincronização em andamento...
                      </Text>
                    </HStack>
                  )}
                  {(displayReport.status === 'error' || displayReport.status === 'failed') &&
                    (displayReport.error || displayReport.errorMessage || displayReport.message) && (
                    <Alert status="error" borderRadius="md" mt={2}>
                      <AlertIcon />
                      <AlertDescription fontSize="xs">
                        {displayReport.error || displayReport.errorMessage || displayReport.message}
                      </AlertDescription>
                    </Alert>
                  )}
                  {displayReport.status === 'created' && displayReport.message && (
                    <Box bg="blue.50" borderRadius="md" px={3} py={2} mt={2} borderWidth="1px" borderColor="blue.100">
                      <Text fontSize="xs" color="blue.700">{displayReport.message}</Text>
                    </Box>
                  )}
                </>
              ) : (
                <Text fontSize="xs" color="gray.500" fontStyle="italic">
                  Nenhuma sincronização realizada ainda. Use o botão Sincronizar acima para iniciar.
                </Text>
              )}
            </Box>
          </Card>
        </Box>

        <Card borderRadius={12} boxShadow="sm" p={4}>
          {isLoading && (
            <Flex justify="center" align="center" py={10}>
              <Spinner size="lg" color={colors.primaryColor} mr={3} />
              <Text color="gray.500" fontSize="sm">Carregando...</Text>
            </Flex>
          )}

          {isErrorErp && !isLoading && (
            <Alert status="error" borderRadius="md" mb={4}>
              <AlertIcon />
              <Box flex="1">
                <AlertTitle fontSize="sm">Erro ao carregar planos</AlertTitle>
                <AlertDescription fontSize="xs">
                  Não foi possível carregar os planos do ERP. Use o botão Sincronizar na seção acima.
                </AlertDescription>
              </Box>
            </Alert>
          )}

          {!isLoading && !isErrorErp && (
            <>
              {(!erpPlans || erpPlans.length === 0) && (
                <Flex direction="column" align="center" py={10}>
                  <Icon as={FaMapSigns} boxSize={10} color="gray.300" mb={3} />
                  <Text fontSize="sm" color="gray.500" mb={1}>Nenhum plano ERP encontrado</Text>
                  <Text fontSize="xs" color="gray.400">
                    Use o botão Sincronizar na seção acima para buscar os planos do ERP.
                  </Text>
                </Flex>
              )}

              {erpPlans && erpPlans.length > 0 && (
                <>
                  <Flex gap={3} mb={3} align="center" flexWrap="wrap">
                    <InputGroup size="sm" flex={1} minW="200px">
                      <InputLeftElement pointerEvents="none">
                        <FaSearch color="gray" />
                      </InputLeftElement>
                      <Input
                        placeholder="Buscar por ID, nome ou slug..."
                        value={searchTerm || ''}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        bg="white"
                        borderColor="gray.200"
                      />
                    </InputGroup>
                    <ValueRangeFilter
                      minValue={minValue}
                      maxValue={maxValue}
                      onMinValueChange={setMinValue}
                      onMaxValueChange={setMaxValue}
                      onClear={clearValueFilter}
                      isActive={isValueFilterActive}
                    />
                    <ClientNumberFilter
                      minClientNumber={minClientNumber}
                      maxClientNumber={maxClientNumber}
                      onMinClientNumberChange={setMinClientNumber}
                      onMaxClientNumberChange={setMaxClientNumber}
                      onClear={clearClientNumberFilter}
                      isActive={isClientNumberFilterActive}
                    />
                    <Tooltip label="Vincular vários planos ERP aos planos de uma vez" hasArrow>
                      <Button
                        leftIcon={<FaLayerGroup />}
                        size="sm"
                        colorScheme="blue"
                        variant="outline"
                        onClick={bulkModal.onOpen}
                        flexShrink={0}
                      >
                        Mapeamento em massa
                      </Button>
                    </Tooltip>
                    <Button leftIcon={<FaFileExport />} size="sm" variant="outline" onClick={handleExportCSV} flexShrink={0}>
                      CSV
                    </Button>
                  </Flex>

                  <HStack spacing={2} mb={4} flexWrap="wrap">
                    <Button
                      size="xs"
                      variant={filter === 'all' ? 'solid' : 'outline'}
                      colorScheme="gray"
                      onClick={() => setFilter('all')}
                      borderRadius="full"
                      fontWeight={filter === 'all' ? '600' : '400'}
                    >
                      Todos ({filteredRows?.length ?? 0})
                    </Button>
                    <Button
                      size="xs"
                      variant={filter === 'mapped' ? 'solid' : 'outline'}
                      colorScheme="green"
                      onClick={() => setFilter('mapped')}
                      borderRadius="full"
                      fontWeight={filter === 'mapped' ? '600' : '400'}
                      leftIcon={<FaCheckCircle />}
                    >
                      Mapeados ({totalMapped})
                    </Button>
                    <Button
                      size="xs"
                      variant={filter === 'unmapped' ? 'solid' : 'outline'}
                      colorScheme="red"
                      onClick={() => setFilter('unmapped')}
                      borderRadius="full"
                      fontWeight={filter === 'unmapped' ? '600' : '400'}
                      leftIcon={<FaTimesCircle />}
                    >
                      Sem mapeamento ({totalUnmapped})
                    </Button>
                    <Box ml="auto" />
                    <HStack spacing={2}>
                      <Button
                        size="xs"
                        variant={sortField === 'price' ? 'solid' : 'outline'}
                        colorScheme="purple"
                        onClick={() => handleSort('price')}
                        leftIcon={
                          <Icon
                            as={
                              sortField === 'price'
                                ? sortDirection === 'asc'
                                  ? FaSortUp
                                  : FaSortDown
                                : FaSort
                            }
                            boxSize={3}
                          />
                        }
                      >
                        Ordenar por Valor
                      </Button>
                      <Button
                        size="xs"
                        variant={sortField === 'count_clients_active' ? 'solid' : 'outline'}
                        colorScheme="green"
                        onClick={() => handleSort('count_clients_active')}
                        leftIcon={
                          <Icon
                            as={
                              sortField === 'count_clients_active'
                                ? sortDirection === 'asc'
                                  ? FaSortUp
                                  : FaSortDown
                                : FaSort
                            }
                            boxSize={3}
                          />
                        }
                      >
                        Ordenar por Ativos
                      </Button>
                    </HStack>
                  </HStack>

                  {rowsToRender.length > 0 ? (
                    <Box
                      ref={listParentRef}
                      h="60vh"
                      minH="400px"
                      overflow="auto"
                      css={{ contain: 'strict' }}
                    >
                      <Box
                        h={`${rowVirtualizer.getTotalSize()}px`}
                        w="100%"
                        position="relative"
                      >
                        <Accordion
                        allowMultiple
                        index={expandedPositions}
                        onChange={(expanded) => handleAccordionChange(expanded as number[])}
                        reduceMotion
                      >
                        {virtualItems.map((virtualRow) => {
                          const row = rowsToRender[virtualRow.index];
                          const rowIndex = virtualRow.index;
                          return (
                            <Box
                              key={row.id ?? row.slug}
                              ref={rowVirtualizer.measureElement}
                              data-index={virtualRow.index}
                              position="absolute"
                              top={0}
                              left={0}
                              w="100%"
                              style={{ transform: `translateY(${virtualRow.start}px)` }}
                            >
                              <AccordionItem
                                mb={1.5}
                                border="1px solid"
                                borderColor={row.isMapped ? 'green.100' : 'gray.200'}
                                borderRadius={6}
                                overflow="hidden"
                              >
                        <AccordionButton py={2.5} px={3} _hover={{ bg: 'gray.50' }}>
                          <Icon
                            as={row.isMapped ? FaCheckCircle : FaTimesCircle}
                            color={row.isMapped ? 'green.400' : 'red.300'}
                            boxSize={4}
                            mr={3}
                            flexShrink={0}
                          />
                          <Box flex="1" textAlign="left" minW={0} mr={4}>
                            <HStack spacing={1} align="center" mb={0.5}>
                              <Text fontWeight="medium" fontSize="sm" lineHeight="1.3" noOfLines={1} flex={1} minW={0}>
                                {row.name}
                              </Text>
                              <Tooltip label="Copiar nome">
                                <IconButton
                                  aria-label="Copiar nome"
                                  size="xs"
                                  variant="ghost"
                                  colorScheme="gray"
                                  icon={<Icon as={FaCopy} boxSize={3} />}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    navigator.clipboard.writeText(row.name || '').then(() => {
                                      toast({ title: 'Nome copiado', status: 'success', duration: 2000, isClosable: true });
                                    });
                                  }}
                                />
                              </Tooltip>
                            </HStack>
                            <HStack spacing={2} align="center" mt={0.5}>
                              {row.id && (
                                <Badge colorScheme="gray" variant="subtle" fontSize="2xs" px={2} py={0.5} borderRadius="md" fontFamily="mono">
                                  ID: {row.id}
                                </Badge>
                              )}
                              {row.isMapped && row.mappings.length > 0 && (
                                <Badge colorScheme="green" variant="subtle" fontSize="2xs" px={2} py={0.5} borderRadius="md">
                                  {row.mappings.length} {row.mappings.length === 1 ? 'mapeamento' : 'mapeamentos'}
                                </Badge>
                              )}
                            </HStack>
                          </Box>
                          <HStack spacing={4} mr={4} flexShrink={0}>
                            {row.price !== undefined && row.price !== null && !Number.isNaN(Number(row.price)) && (
                              <Box textAlign="center" minW="100px">
                                <Text fontSize="2xs" color="gray.500" fontWeight="500" mb={0.5}>
                                  Valor
                                </Text>
                                <Text fontSize="sm" fontWeight="700" color="purple.600">
                                  {formatPrice(row.price)}
                                </Text>
                              </Box>
                            )}
                            {row.count_clients_active != null && (
                              <Box textAlign="center" minW="90px">
                                <Text fontSize="2xs" color="gray.500" fontWeight="500" mb={0.5}>
                                  Ativos
                                </Text>
                                <Text fontSize="sm" fontWeight="700" color="green.600">
                                  {row.count_clients_active.toLocaleString('pt-BR')}
                                </Text>
                              </Box>
                            )}
                          </HStack>
                          <HStack spacing={2} flexShrink={0}>
                            <Button
                              size="xs"
                              variant="ghost"
                              colorScheme="blue"
                              leftIcon={<FaPlus />}
                              onClick={(e) => {
                                e.stopPropagation();
                                modalRef.current?.open({
                                  plan_slug_integration: getPlanSlugIntegrationForPayload(row),
                                });
                              }}
                            >
                              Mapear
                            </Button>
                            <AccordionIcon />
                          </HStack>
                        </AccordionButton>

                        <AccordionPanel pb={3} px={3} bg="gray.50">
                          {!expandedAccordionIndexes.includes(rowIndex) ? null : row.mappings.length === 0 ? (
                            <Flex align="center" justify="center" py={4} direction="column">
                              <Text fontSize="xs" color="gray.400" fontStyle="italic" mb={2}>
                                Nenhum plano vinculado a este slug.
                              </Text>
                              <Button
                                size="xs"
                                colorScheme="blue"
                                variant="outline"
                                leftIcon={<FaPlus />}
                                onClick={() =>
                                  modalRef.current?.open({
                                    plan_slug_integration: getPlanSlugIntegrationForPayload(row),
                                  })
                                }
                              >
                                Criar mapeamento
                              </Button>
                            </Flex>
                          ) : (
                            <Table size="sm" variant="simple" bg="white" borderRadius={6} overflow="hidden">
                              <Thead bg="gray.100">
                                <Tr>
                                  <Th fontSize="2xs" px={3} py={2} color="gray.500">Plano</Th>
                                  <Th fontSize="2xs" px={3} py={2} color="gray.500">Slug Integração</Th>
                                  <Th fontSize="2xs" px={3} py={2} color="gray.500">Status</Th>
                                  <Th fontSize="2xs" px={3} py={2} color="gray.500">Criado em</Th>
                                  <Th fontSize="2xs" px={3} py={2} color="gray.500" w="60px">Ações</Th>
                                </Tr>
                              </Thead>
                              <Tbody>
                                {row.mappings.map((m) => (
                                  <Tr key={m.id} _hover={{ bg: 'gray.50' }}>
                                    <Td px={3} py={2} fontSize="xs">
                                      <Text fontWeight="medium" mb={1}>{getPlanName(m.plan, plansMap)}</Text>
                                      <HStack spacing={2} align="center" mt={0.5}>
                                        <Badge
                                          colorScheme="gray"
                                          variant="subtle"
                                          fontSize="2xs"
                                          px={2}
                                          py={0.5}
                                          borderRadius="md"
                                          fontFamily="mono"
                                        >
                                          ID: {m.plan}
                                        </Badge>
                                      </HStack>
                                    </Td>
                                    <Td px={3} py={2} fontSize="xs" color="gray.600" fontFamily="mono">
                                      {m.plan_slug_integration}
                                    </Td>
                                    <Td px={3} py={2}>
                                      <Badge colorScheme={m.active ? 'green' : 'red'} variant="subtle" fontSize="2xs">
                                        {m.active ? 'Ativo' : 'Inativo'}
                                      </Badge>
                                    </Td>
                                    <Td px={3} py={2} fontSize="xs" color="gray.500">
                                      {formatDate(m.created_at)}
                                    </Td>
                                    <Td px={3} py={2}>
                                      <Tooltip label="Excluir mapeamento" hasArrow>
                                        <IconButton
                                          aria-label="Excluir mapeamento"
                                          icon={<FaTrash />}
                                          size="xs"
                                          variant="ghost"
                                          colorScheme="red"
                                          onClick={() => handleOpenDelete(m)}
                                          isDisabled={deleteMappingMutation.isLoading}
                                        />
                                      </Tooltip>
                                    </Td>
                                  </Tr>
                                ))}
                              </Tbody>
                            </Table>
                          )}
                        </AccordionPanel>
                              </AccordionItem>
                            </Box>
                          );
                        })}
                        </Accordion>
                      </Box>
                    </Box>
                  ) : (
                    <Flex direction="column" align="center" py={8}>
                      <Text color="gray.400" fontSize="sm">
                        {searchTerm?.trim() || isValueFilterActive || isClientNumberFilterActive
                          ? 'Nenhum plano encontrado com os filtros aplicados.'
                          : filter === 'mapped'
                            ? 'Nenhum plano mapeado encontrado.'
                            : filter === 'unmapped'
                              ? 'Nenhum plano sem mapeamento encontrado.'
                              : 'Nenhum plano encontrado.'}
                      </Text>
                    </Flex>
                  )}
                </>
              )}
            </>
          )}
        </Card>
      </Flex>

      <CreateMapIntegrationPlanModal ref={modalRef} agreementId={id ?? undefined} />
      <BulkMapIntegrationPlanModal
        isOpen={bulkModal.isOpen}
        onClose={bulkModal.onClose}
        agreementId={id ?? undefined}
      />

      <Modal isOpen={syncModal.isOpen} onClose={syncModal.onClose} isCentered size="md">
        <ModalOverlay bg="blackAlpha.400" backdropFilter="blur(4px)" />
        <ModalContent borderRadius={12} mx={4}>
          <ModalCloseButton />
          <ModalHeader pt={6} pb={2} textAlign="center">
            <Flex direction="column" align="center">
              <Flex w={14} h={14} borderRadius="full" bg="blue.50" align="center" justify="center" mb={3}>
                <Icon as={FaSyncAlt} boxSize={6} color="blue.500" />
              </Flex>
              <Text fontSize="lg" fontWeight="600" color="gray.700">Sincronização iniciada</Text>
            </Flex>
          </ModalHeader>
          <ModalBody textAlign="center" px={8} pb={2}>
            <Text fontSize="sm" color="gray.600" mb={2}>
              A sincronização está rodando em segundo plano. Acompanhe o status na seção abaixo.
            </Text>
            <Text fontSize="xs" color="gray.500">
              Você pode fechar este modal. O processo continua em andamento.
            </Text>
          </ModalBody>
          <ModalFooter justifyContent="center" gap={3} pt={4} pb={6}>
            <Button variant="ghost" size="sm" onClick={syncModal.onClose} color="gray.500">
              Fechar
            </Button>
            <Button
              leftIcon={<FaClipboardList />}
              colorScheme="blue"
              size="sm"
              onClick={() => {
                syncModal.onClose();
                navigate('/relatorios');
              }}
            >
              Ir para Relatórios
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      <AlertDialog
        isOpen={deleteDialog.isOpen}
        leastDestructiveRef={cancelDeleteRef}
        onClose={() => {
          deleteDialog.onClose();
          setMappingToDelete(null);
        }}
        isCentered
      >
        <AlertDialogOverlay>
          <AlertDialogContent borderRadius="lg">
            <AlertDialogHeader fontSize="md" fontWeight="semibold">
              Excluir mapeamento?
            </AlertDialogHeader>
            <AlertDialogBody>
              {mappingToDelete && (
                <Text fontSize="sm" color="gray.600">
                  O vínculo entre o plano <strong>{getPlanName(mappingToDelete.plan, plansMap)}</strong> e o slug
                  <strong> {mappingToDelete.plan_slug_integration}</strong> será removido. Esta ação não pode ser desfeita.
                </Text>
              )}
            </AlertDialogBody>
            <AlertDialogFooter>
              <Button ref={cancelDeleteRef} onClick={deleteDialog.onClose} variant="ghost" size="sm">
                Cancelar
              </Button>
              <Button
                colorScheme="red"
                size="sm"
                onClick={handleConfirmDelete}
                isLoading={deleteMappingMutation.isLoading}
                loadingText="Excluindo..."
              >
                Excluir
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </>
  );
}

export { PlanMappingPage as PlanMappingContent };
export default PlanMappingPage;
